import { useState } from 'react';
import { api } from './api.js';

/**
 * Changing your own password.
 *
 * The gap this closes: an administrator creates an account and hands over a first password,
 * and until now that was the password that person had forever. Every account in the system
 * was therefore known to at least two people, permanently — which is fine for a demonstration
 * and not fine for anybody's real incident record.
 *
 * The current password is asked for even though the session is already authenticated. That
 * is not theatre here: it is what stops somebody who walks up to an unlocked laptop from
 * taking the account with them.
 */
export function ChangePassword({ onClose }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [again, setAgain] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  const tooShort = next.length > 0 && next.length < 12;
  const mismatch = again.length > 0 && next !== again;
  const ready = current && next.length >= 12 && next === again;

  async function submit(e) {
    e.preventDefault();
    if (!ready) return;
    setBusy(true); setError(null);
    try {
      await api.changePassword(current, next);
      setDone(true);
    } catch (err) {
      setError(err.code === 'current_password_wrong'
        ? 'That is not your current password.'
        : err.detail || 'That did not save.');
    } finally { setBusy(false); }
  }

  return (
    <div className="hoWrap" onClick={onClose}>
      <div className="ho narrow" onClick={(e) => e.stopPropagation()}>
        <div className="hoHead">
          <div>
            <h3>Change your password</h3>
            <p>{done ? 'Done' : 'At least twelve characters'}</p>
          </div>
          <button className="hoX" onClick={onClose} aria-label="Close">×</button>
        </div>

        {done ? (
          <div className="hoBody form">
            <p className="okNote">
              Changed. Any other device you were signed in on has been signed out — if
              somebody else knew the old password, they are out too.
            </p>
            <div className="actions">
              <span className="spacer" />
              <button className="btn narrow" onClick={onClose}>Close</button>
            </div>
          </div>
        ) : (
          <form className="hoBody form" onSubmit={submit}>
            <label className="f">
              <span>Current password</span>
              <input type="password" value={current} autoComplete="current-password"
                     onChange={(e) => setCurrent(e.target.value)} required autoFocus />
            </label>

            <label className="f">
              <span>New password</span>
              <input type="password" value={next} autoComplete="new-password"
                     onChange={(e) => setNext(e.target.value)} minLength={12} required />
              {/* Length rather than a mix of symbols. Complexity rules mostly produce
                  Passw0rd! on a note stuck to the monitor. */}
              <small className={tooShort ? 'warnNote' : ''}>
                {tooShort ? `${12 - next.length} more to go` : 'Three or four unrelated words works well'}
              </small>
            </label>

            <label className="f">
              <span>New password again</span>
              <input type="password" value={again} autoComplete="new-password"
                     onChange={(e) => setAgain(e.target.value)} required />
              {mismatch && <small className="warnNote">These two do not match</small>}
            </label>

            {error && <p className="err">{error}</p>}

            <div className="actions">
              <span className="spacer" />
              <button type="button" className="ghost" onClick={onClose}>Cancel</button>
              <button className="btn narrow" type="submit" disabled={busy || !ready}>
                {busy ? 'Saving…' : 'Change it'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
